<template>
  <div class="upsell-modal">
    <HMModalHeader
      v-if="!hideHeader"
      :title="getTitle"
      :closable="false"
      @close="close"
    >
      <template slot="title">
        <div class="d-flex flex-column text-center w-100">
          <HMCountdown
            class="c-brand-color countdown-timer mb-2"
            :date="getUpsellExpiration"
            :displayHoursAlways="true"
            :displayMinutesAlways="true"
            @end="countdownEnded"
          />
          <h4>{{ getTitle }}</h4>
        </div>
      </template>
    </HMModalHeader>
    <div v-if="product.upsell_media">
      <HMFilePreview :file="product.upsell_media.media" />
    </div>
    <div v-else>
      <HMFixedAspectRatioContainer>
        <div class="image" :style="style"></div>
      </HMFixedAspectRatioContainer>
    </div>
    <div class="d-flex flex-row justify-content-between flex-nowrap mt-3">
      <div class="mr-4">
        <h4 class="c-charcoal">
          {{ product.upsell_product.product_name }}
        </h4>
        <div v-if="getDescription" class="mt-1 small">
          {{ getDescription }}
        </div>
      </div>
      <div class="flex-nowrap">
        <div class="text-right">
          <span
            class="line-through"
            v-if="
              !isUpsellProductRecurring ||
              upsellRecurringOptionExistInRegularPriceRecurringOptions
            "
          >
            {{ standardPriceWithTaxText }}
          </span>
        </div>
        <span class="c-charcoal text-nowrap">
          {{
            $t('STUDIO.LABELS.ONLY_PRICE', {
              price: upsellPriceWithTaxText,
            })
          }}
        </span>
      </div>
    </div>
    <HMButton
      v-if="isStripeProcessor"
      class="w-100 text-center mt-4"
      color="brand"
      @click="purchase"
      :disabled="isPurchasing || preview"
      :loading="isPurchasing"
    >
      <span class="w-100 text-center">
        {{ $t(buyButtonText) }}
      </span>
    </HMButton>
    <div v-if="isPayPalProcessor" class="w-100 mt-5">
      <div class="mt20">
        <form name="_xclick" :action="getPayPalCheckoutUrl" method="post">
          <input type="hidden" name="business" :value="product.paypal_email" />
          <input
            type="hidden"
            name="currency_code"
            :value="product.currency || 'USD'"
          />
          <input
            type="hidden"
            name="item_name"
            :value="product.upsell_product.product_name"
          />
          <input type="hidden" name="notify_url" :value="getPayPalNotifyUrl" />
          <input type="hidden" name="return" :value="getPayPalReturnUrl" />
          <input
            type="hidden"
            name="custom"
            :value="getPayPalCustomParametersField"
          />
          <input type="hidden" name="no_shipping" value="1" />
          <div v-if="!isUpsellProductRecurring">
            <input type="hidden" name="cmd" value="_xclick" />
            <input
              type="hidden"
              name="amount"
              :value="formatPayPalPrice(upsellPriceWithTax)"
            />
          </div>
          <div v-if="isUpsellProductRecurring">
            <div v-if="product.upsell_product.trial">
              <input type="hidden" name="a1" value="0" />
              <input
                type="hidden"
                name="p1"
                :value="product.upsell_product.trial_units"
              />
              <input
                type="hidden"
                name="t1"
                :value="product.upsell_product.trial_period"
              />
            </div>
            <input type="hidden" name="cmd" value="_xclick-subscriptions" />
            <input
              type="hidden"
              name="a3"
              :value="formatPayPalPrice(upsellPriceWithTax)"
            />
            <input
              type="hidden"
              name="p3"
              :value="getRecurringPayPalBillingInterval"
            />
            <input type="hidden" name="t3" :value="getRecurringPayPalPeriod" />
            <input type="hidden" name="src" value="1" />
          </div>
          <HMButton
            class="w-100"
            color="paypal"
            type="submit"
            :disabled="preview"
          >
            <div class="w-100">
              <span>{{ $t('PRODUCTS.LABELS.CHECK_OUT_WITH') }}</span>
              <img
                :src="require('@/assets/images/PayPal.png')"
                class="ml-2 img-paypal"
              />
            </div>
          </HMButton>
        </form>
      </div>
    </div>
    <HMButton
      class="w-100 text-center mt-2"
      color="outline-gray"
      @click="close"
    >
      <span class="w-100 text-center">
        {{ $t('PRODUCTS.BUTTON.DONT_WANT_THIS_SPECIAL_OFFER') }}
      </span>
    </HMButton>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { getDefaultThumbnailForProductType } from '../../utils/media';
import HMFilePreview from '../common/HMFilePreview';
import { errorToMessage } from '../../../core/utils/errors';
import { priceWithTax } from '../../../core/utils/utils';
import { removeTrailingSlash } from '@/core/utils/utils';
import { mapGetters } from 'vuex';
import { getRecurringOptions } from '@/core/utils/products';

const RECURRING_OPTIONS = getRecurringOptions();

export default Vue.extend({
  name: 'ProductUpsellModal',
  components: {
    HMFilePreview,
  },
  props: {
    product: {
      type: Object,
    },
    studioURL: {
      type: String,
    },
    onClose: {
      type: Function,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    buyButtonText: {
      type: String,
      default: 'PRODUCTS.BUTTON.PURCHASE_NOW',
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      user: 'user/user',
    }),
    getTitle() {
      return (
        this.product?.upsell_title ||
        this.$t('PRODUCTS.TITLE.LIMITED_TIME_OFFER')
      );
    },
    getDescription() {
      return this.product?.upsell_description;
    },
    getUpsellExpiration() {
      return moment()
        .add(this.product?.upsell_seconds_remaining, 'seconds')
        .toDate();
    },
    style() {
      if (this.product?.upsell_product?.thumbnail_file) {
        const thumbnailUrl = this.product.upsell_product.thumbnail_file
          .thumbnail;
        return {
          backgroundImage: 'url(' + thumbnailUrl + ')',
          backgroundSize: 'cover',
        };
      }
      if (this.product?.upsell_product?.cover_file) {
        const thumbnailUrl = this.product.upsell_product.cover_file.thumbnail;
        return {
          backgroundImage: 'url(' + thumbnailUrl + ')',
          backgroundSize: 'cover',
        };
      }
      const thumbnailUrl = getDefaultThumbnailForProductType(
        this.product?.upsell_product?.product_type
      );
      return {
        backgroundImage: 'url(' + thumbnailUrl + ')',
      };
    },
    isUpsellProductRecurring() {
      return this.product.upsell_product.pricing_type === 'recurring';
    },
    isUpsellProductMultipleRecurring() {
      return this.product.upsell_product.recurring_type === 'multiple';
    },
    upsellProduct() {
      return this.product.upsell_product;
    },
    upsellRecurringOption() {
      if (this.isUpsellProductMultipleRecurring) {
        return this.upsellProduct.recurring_options[
          this.product.upsell_recurring_type
        ];
      } else {
        return {
          available: true,
          price: this.upsellProduct.price,
        };
      }
    },
    upsellRecurringOptionExistInRegularPriceRecurringOptions() {
      if (this.isUpsellProductMultipleRecurring) {
        return !!this.upsellRecurringOption?.available;
      } else {
        return (
          this.upsellProduct.recurring_type ===
          this.product.upsell_recurring_type
        );
      }
    },
    standardPriceWithTax() {
      if (this.isUpsellProductRecurring) {
        return priceWithTax(
          this.upsellRecurringOption.price || 0,
          this.product.upsell_product.tax_rate_percent
        );
      } else {
        return priceWithTax(
          this.product.upsell_product.price,
          this.product.upsell_product.tax_rate_percent
        );
      }
    },
    standardPriceWithTaxText() {
      if (this.isUpsellProductRecurring) {
        return this.$t(this.getRecurringPricingIntervalTranslation, {
          price: this.$formatCurrency(
            this.standardPriceWithTax,
            this.product.currency
          ),
        });
      } else {
        return this.$formatCurrency(
          this.standardPriceWithTax,
          this.product.currency
        );
      }
    },
    upsellPriceWithTax() {
      return priceWithTax(
        this.product.upsell_price,
        this.product.upsell_product.tax_rate_percent
      );
    },
    upsellPriceWithTaxText() {
      if (this.isUpsellProductRecurring) {
        return this.$t(this.getRecurringPricingIntervalTranslation, {
          price: this.$formatCurrency(
            this.upsellPriceWithTax,
            this.product.currency
          ),
        });
      } else {
        return this.$formatCurrency(
          this.upsellPriceWithTax,
          this.product.currency
        );
      }
    },
    getRecurringPricingIntervalTranslation() {
      if (!this.isUpsellProductRecurring) return '';
      return RECURRING_OPTIONS[this.product.upsell_recurring_type]
        .priceTranslation;
    },
    getRecurringPayPalBillingInterval() {
      if (!this.isUpsellProductRecurring) return '';
      return RECURRING_OPTIONS[this.product.upsell_recurring_type]
        .billingInterval;
    },
    getRecurringPayPalPeriod() {
      if (!this.isUpsellProductRecurring) return '';
      return RECURRING_OPTIONS[this.product.upsell_recurring_type].payPalPeriod;
    },
    isPayPalProcessor() {
      if (!this.product) return false;
      return this.product.payment_gateway === 'paypal';
    },
    isStripeProcessor() {
      if (!this.product) return false;
      return this.product.payment_gateway === 'stripe';
    },
    getPayPalCheckoutUrl() {
      return `https://www${
        process.env.VUE_APP_PAYPAL_SANDBOX === 'true' ? '.sandbox' : ''
      }.paypal.com/us/cgi-bin/webscr`;
    },
    getPayPalNotifyUrl() {
      return `${removeTrailingSlash(
        process.env.VUE_APP_API_BASE_URL
      )}/paypal-callback`;
    },
    getPayPalReturnUrl() {
      return `${removeTrailingSlash(
        process.env.VUE_APP_API_BASE_URL
      )}/paypal-post-purchase/${this.product.upsell_product.id}`;
    },
    getPayPalCustomParametersField() {
      return `?product_id=${this.product.upsell_product.id}&student_id=${
        this.user.student.id
      }&coupon_code=&recurring_option=${
        this.isUpsellProductRecurring ? this.product.upsell_recurring_type : ''
      }`;
    },
  },
  data() {
    return {
      isPurchasing: false,
    };
  },
  methods: {
    formatPayPalPrice(price) {
      return price.toFixed(2);
    },
    close() {
      if (this.onClose) this.onClose();
      this.$emit('close');
    },
    countdownEnded() {
      this.close();
    },
    async purchase() {
      try {
        this.isPurchasing = true;
        await this.$store.dispatch('product/purchaseUpsellProduct', {
          productId: this.product.id,
          studioURL: this.studioURL,
          upsellProductType: this.product.upsell_product.product_type,
        });
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'STUDIO.NOTIFICATIONS.UPSELL_PRODUCT_SUCCESSFULLY_PURCHASED'
          ),
        });
        this.close();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Upsell purchase failed',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isPurchasing = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.image {
  position: relative;
  border-radius: $border-radius;
  background-position: center center;
  background-size: auto 65%;
  background-repeat: no-repeat;
  background-color: var(
    --brand-color-with-opacity-03,
    rgba($light-bubblegum, 0.3)
  );
  object-fit: cover;
  transition: all 0.3s ease-in-out;
}

.line-through {
  text-decoration: line-through;
}
h4 {
  font-weight: 500;
}
.countdown-timer {
  font-size: 25px;
  font-weight: 600;
}
.hm-modal-header {
  padding-top: 0;
  border-bottom: none;
}

.img-paypal {
  height: 22px;
  width: auto;
}
</style>
